import {
	type Action,
	type BoundActions,
	createActionsHook,
	createContainer,
	createHook,
	createStore,
    type GenericContainerComponent,
    type HookActionsFunction,
    type HookFunction,
} from 'react-sweet-state';

import { type UserSearchItem } from '@atlaskit/smart-common';
import { type AutoSuggestionResult } from '@atlassian/dynamic-cards-search-client';

import { type SearchLinkProps } from '../../../common/ui/search-link';
import { type SearchResultCount } from '../../../common/utils/events/dropdown-results-shown';

type PeopleAndTeamsResults = {
	duration: number;
	results: UserSearchItem[];
};

type AutoSuggestionsResults = {
	duration: number;
	results: AutoSuggestionResult[];
};

type SelectedInputFilters = {
	displayText: string;
	id: string;
	onSubmit: () => {};
};

export type TState = {
	autoSuggestionResults: AutoSuggestionsResults;
	dropdownLoading: boolean;
	peopleAndTeamsResults: PeopleAndTeamsResults;
	prefetchedRecentSearchResults: SearchLinkProps[];
	prefetchedRecentViewsResults: SearchLinkProps[] | undefined;
	quickSearchResults: SearchLinkProps[];
	recentSearchResults: SearchLinkProps[];
	recentViewsResults: SearchLinkProps[];
	resultCount: SearchResultCount;
	selectedInputFilters: SelectedInputFilters[];
	showDropdown: boolean;
};

/**
 * This state object stores the results generated/displayed for each result type.
 */
export const initialState: {
    autoSuggestionResults: {
        duration: number;
        results: never[];
    }; dropdownLoading: boolean; peopleAndTeamsResults: {
        duration: number;
        results: never[];
    }; prefetchedRecentSearchResults: never[]; prefetchedRecentViewsResults: undefined; quickSearchResults: never[]; recentSearchResults: never[]; recentViewsResults: never[]; resultCount: {
        autoSuggestion: number;
        peopleAndTeams: number;
        quickSearch: number;
        recentSearch: number;
        recentViews: number;
    }; selectedInputFilters: never[]; showDropdown: boolean;
} = {
	showDropdown: false,
	dropdownLoading: true,
	prefetchedRecentViewsResults: undefined,
	prefetchedRecentSearchResults: [],
	peopleAndTeamsResults: {
		results: [],
		duration: -1,
	},
	recentViewsResults: [],
	autoSuggestionResults: {
		results: [],
		duration: -1,
	},
	quickSearchResults: [],
	recentSearchResults: [],
	resultCount: {
		peopleAndTeams: 0,
		autoSuggestion: 0,
		quickSearch: 0,
		recentSearch: 0,
		recentViews: 0,
	},
	selectedInputFilters: [],
};

const actions = {
	setShowDropdown:
		(showDropdown: boolean): Action<TState> =>
		({ setState }) => {
			setState({ showDropdown });
		},
	setDropdownLoading:
		(dropdownLoading: boolean): Action<TState> =>
		({ setState }) => {
			setState({ dropdownLoading });
		},
	setPeopleAndTeamsResults:
		(peopleAndTeamsResults: PeopleAndTeamsResults): Action<TState> =>
		({ setState }) => {
			setState({ peopleAndTeamsResults });
		},
	setRecentViewsResults:
		(recentViewsResults: SearchLinkProps[]): Action<TState> =>
		({ setState }) => {
			setState({ recentViewsResults });
		},
	setAutoSuggestionResults:
		(autoSuggestionResults: AutoSuggestionsResults): Action<TState> =>
		({ setState }) => {
			setState({ autoSuggestionResults });
		},
	setRecentSearchResults:
		(recentSearchResults: SearchLinkProps[]): Action<TState> =>
		({ setState }) => {
			setState({ recentSearchResults });
		},
	setQuickSearchResults:
		(quickSearchResults: SearchLinkProps[]): Action<TState> =>
		({ setState }) => {
			setState({ quickSearchResults });
		},
	setResultCount:
		(resultCount: SearchResultCount): Action<TState> =>
		({ setState }) => {
			setState({ resultCount });
		},
	setPrefetchedRecentViewsResults:
		(prefetchedRecentViewsResults: SearchLinkProps[]): Action<TState> =>
		({ setState }) => {
			setState({ prefetchedRecentViewsResults });
		},
	setPrefetchedRecentSearchResults:
		(prefetchedRecentSearchResults: SearchLinkProps[]): Action<TState> =>
		({ setState }) => {
			setState({ prefetchedRecentSearchResults });
		},
	setSelectedInputFilters:
		(selectedInputFilters: SelectedInputFilters[]): Action<TState> =>
		({ setState }) => {
			setState({ selectedInputFilters });
		},
};

type TActions = typeof actions;

export const DropdownStoreContainer: GenericContainerComponent<unknown> = createContainer();

// Keep store private and use hooks to access state and actions
const Store = createStore<TState, TActions>({
	containedBy: DropdownStoreContainer,
	initialState,
	actions,
	name: 'dynamic-cards-dropdown',
});

// Hooks

export const useDropdownActions: HookActionsFunction<BoundActions<TState, {
    setAutoSuggestionResults: (autoSuggestionResults: AutoSuggestionsResults) => Action<TState>;
    setDropdownLoading: (dropdownLoading: boolean) => Action<TState>;
    setPeopleAndTeamsResults: (peopleAndTeamsResults: PeopleAndTeamsResults) => Action<TState>;
    setPrefetchedRecentSearchResults: (prefetchedRecentSearchResults: SearchLinkProps[]) => Action<TState>;
    setPrefetchedRecentViewsResults: (prefetchedRecentViewsResults: SearchLinkProps[]) => Action<TState>;
    setQuickSearchResults: (quickSearchResults: SearchLinkProps[]) => Action<TState>;
    setRecentSearchResults: (recentSearchResults: SearchLinkProps[]) => Action<TState>;
    setRecentViewsResults: (recentViewsResults: SearchLinkProps[]) => Action<TState>;
    setResultCount: (resultCount: SearchResultCount) => Action<TState>;
    setSelectedInputFilters: (selectedInputFilters: SelectedInputFilters[]) => Action<TState>;
    setShowDropdown: (showDropdown: boolean) => Action<TState>;
}>> = createActionsHook(Store);

export const useDropdown: HookFunction<boolean, BoundActions<TState, {
    setAutoSuggestionResults: (autoSuggestionResults: AutoSuggestionsResults) => Action<TState>;
    setDropdownLoading: (dropdownLoading: boolean) => Action<TState>;
    setPeopleAndTeamsResults: (peopleAndTeamsResults: PeopleAndTeamsResults) => Action<TState>;
    setPrefetchedRecentSearchResults: (prefetchedRecentSearchResults: SearchLinkProps[]) => Action<TState>;
    setPrefetchedRecentViewsResults: (prefetchedRecentViewsResults: SearchLinkProps[]) => Action<TState>;
    setQuickSearchResults: (quickSearchResults: SearchLinkProps[]) => Action<TState>;
    setRecentSearchResults: (recentSearchResults: SearchLinkProps[]) => Action<TState>;
    setRecentViewsResults: (recentViewsResults: SearchLinkProps[]) => Action<TState>;
    setResultCount: (resultCount: SearchResultCount) => Action<TState>;
    setSelectedInputFilters: (selectedInputFilters: SelectedInputFilters[]) => Action<TState>;
    setShowDropdown: (showDropdown: boolean) => Action<TState>;
}>, void> = createHook(Store, {
	selector: (state) => state.showDropdown,
});

export const useDropdownLoading: HookFunction<boolean, BoundActions<TState, {
    setAutoSuggestionResults: (autoSuggestionResults: AutoSuggestionsResults) => Action<TState>;
    setDropdownLoading: (dropdownLoading: boolean) => Action<TState>;
    setPeopleAndTeamsResults: (peopleAndTeamsResults: PeopleAndTeamsResults) => Action<TState>;
    setPrefetchedRecentSearchResults: (prefetchedRecentSearchResults: SearchLinkProps[]) => Action<TState>;
    setPrefetchedRecentViewsResults: (prefetchedRecentViewsResults: SearchLinkProps[]) => Action<TState>;
    setQuickSearchResults: (quickSearchResults: SearchLinkProps[]) => Action<TState>;
    setRecentSearchResults: (recentSearchResults: SearchLinkProps[]) => Action<TState>;
    setRecentViewsResults: (recentViewsResults: SearchLinkProps[]) => Action<TState>;
    setResultCount: (resultCount: SearchResultCount) => Action<TState>;
    setSelectedInputFilters: (selectedInputFilters: SelectedInputFilters[]) => Action<TState>;
    setShowDropdown: (showDropdown: boolean) => Action<TState>;
}>, void> = createHook(Store, {
	selector: (state) => state.dropdownLoading,
});

export const usePeopleAndTeamsResults: HookFunction<PeopleAndTeamsResults, BoundActions<TState, {
    setAutoSuggestionResults: (autoSuggestionResults: AutoSuggestionsResults) => Action<TState>;
    setDropdownLoading: (dropdownLoading: boolean) => Action<TState>;
    setPeopleAndTeamsResults: (peopleAndTeamsResults: PeopleAndTeamsResults) => Action<TState>;
    setPrefetchedRecentSearchResults: (prefetchedRecentSearchResults: SearchLinkProps[]) => Action<TState>;
    setPrefetchedRecentViewsResults: (prefetchedRecentViewsResults: SearchLinkProps[]) => Action<TState>;
    setQuickSearchResults: (quickSearchResults: SearchLinkProps[]) => Action<TState>;
    setRecentSearchResults: (recentSearchResults: SearchLinkProps[]) => Action<TState>;
    setRecentViewsResults: (recentViewsResults: SearchLinkProps[]) => Action<TState>;
    setResultCount: (resultCount: SearchResultCount) => Action<TState>;
    setSelectedInputFilters: (selectedInputFilters: SelectedInputFilters[]) => Action<TState>;
    setShowDropdown: (showDropdown: boolean) => Action<TState>;
}>, void> = createHook(Store, {
	selector: (state) => state.peopleAndTeamsResults,
});

export const useRecentViewsResults: HookFunction<SearchLinkProps[], BoundActions<TState, {
    setAutoSuggestionResults: (autoSuggestionResults: AutoSuggestionsResults) => Action<TState>;
    setDropdownLoading: (dropdownLoading: boolean) => Action<TState>;
    setPeopleAndTeamsResults: (peopleAndTeamsResults: PeopleAndTeamsResults) => Action<TState>;
    setPrefetchedRecentSearchResults: (prefetchedRecentSearchResults: SearchLinkProps[]) => Action<TState>;
    setPrefetchedRecentViewsResults: (prefetchedRecentViewsResults: SearchLinkProps[]) => Action<TState>;
    setQuickSearchResults: (quickSearchResults: SearchLinkProps[]) => Action<TState>;
    setRecentSearchResults: (recentSearchResults: SearchLinkProps[]) => Action<TState>;
    setRecentViewsResults: (recentViewsResults: SearchLinkProps[]) => Action<TState>;
    setResultCount: (resultCount: SearchResultCount) => Action<TState>;
    setSelectedInputFilters: (selectedInputFilters: SelectedInputFilters[]) => Action<TState>;
    setShowDropdown: (showDropdown: boolean) => Action<TState>;
}>, void> = createHook(Store, {
	selector: (state) => state.recentViewsResults,
});

export const useAutoSuggestionResults: HookFunction<AutoSuggestionsResults, BoundActions<TState, {
    setAutoSuggestionResults: (autoSuggestionResults: AutoSuggestionsResults) => Action<TState>;
    setDropdownLoading: (dropdownLoading: boolean) => Action<TState>;
    setPeopleAndTeamsResults: (peopleAndTeamsResults: PeopleAndTeamsResults) => Action<TState>;
    setPrefetchedRecentSearchResults: (prefetchedRecentSearchResults: SearchLinkProps[]) => Action<TState>;
    setPrefetchedRecentViewsResults: (prefetchedRecentViewsResults: SearchLinkProps[]) => Action<TState>;
    setQuickSearchResults: (quickSearchResults: SearchLinkProps[]) => Action<TState>;
    setRecentSearchResults: (recentSearchResults: SearchLinkProps[]) => Action<TState>;
    setRecentViewsResults: (recentViewsResults: SearchLinkProps[]) => Action<TState>;
    setResultCount: (resultCount: SearchResultCount) => Action<TState>;
    setSelectedInputFilters: (selectedInputFilters: SelectedInputFilters[]) => Action<TState>;
    setShowDropdown: (showDropdown: boolean) => Action<TState>;
}>, void> = createHook(Store, {
	selector: (state) => state.autoSuggestionResults,
});

export const useRecentSearchResults: HookFunction<SearchLinkProps[], BoundActions<TState, {
    setAutoSuggestionResults: (autoSuggestionResults: AutoSuggestionsResults) => Action<TState>;
    setDropdownLoading: (dropdownLoading: boolean) => Action<TState>;
    setPeopleAndTeamsResults: (peopleAndTeamsResults: PeopleAndTeamsResults) => Action<TState>;
    setPrefetchedRecentSearchResults: (prefetchedRecentSearchResults: SearchLinkProps[]) => Action<TState>;
    setPrefetchedRecentViewsResults: (prefetchedRecentViewsResults: SearchLinkProps[]) => Action<TState>;
    setQuickSearchResults: (quickSearchResults: SearchLinkProps[]) => Action<TState>;
    setRecentSearchResults: (recentSearchResults: SearchLinkProps[]) => Action<TState>;
    setRecentViewsResults: (recentViewsResults: SearchLinkProps[]) => Action<TState>;
    setResultCount: (resultCount: SearchResultCount) => Action<TState>;
    setSelectedInputFilters: (selectedInputFilters: SelectedInputFilters[]) => Action<TState>;
    setShowDropdown: (showDropdown: boolean) => Action<TState>;
}>, void> = createHook(Store, {
	selector: (state) => state.recentSearchResults,
});

export const useQuickSearchResults: HookFunction<SearchLinkProps[], BoundActions<TState, {
    setAutoSuggestionResults: (autoSuggestionResults: AutoSuggestionsResults) => Action<TState>;
    setDropdownLoading: (dropdownLoading: boolean) => Action<TState>;
    setPeopleAndTeamsResults: (peopleAndTeamsResults: PeopleAndTeamsResults) => Action<TState>;
    setPrefetchedRecentSearchResults: (prefetchedRecentSearchResults: SearchLinkProps[]) => Action<TState>;
    setPrefetchedRecentViewsResults: (prefetchedRecentViewsResults: SearchLinkProps[]) => Action<TState>;
    setQuickSearchResults: (quickSearchResults: SearchLinkProps[]) => Action<TState>;
    setRecentSearchResults: (recentSearchResults: SearchLinkProps[]) => Action<TState>;
    setRecentViewsResults: (recentViewsResults: SearchLinkProps[]) => Action<TState>;
    setResultCount: (resultCount: SearchResultCount) => Action<TState>;
    setSelectedInputFilters: (selectedInputFilters: SelectedInputFilters[]) => Action<TState>;
    setShowDropdown: (showDropdown: boolean) => Action<TState>;
}>, void> = createHook(Store, {
	selector: (state) => state.quickSearchResults,
});

export const useResultCount: HookFunction<SearchResultCount, BoundActions<TState, {
    setAutoSuggestionResults: (autoSuggestionResults: AutoSuggestionsResults) => Action<TState>;
    setDropdownLoading: (dropdownLoading: boolean) => Action<TState>;
    setPeopleAndTeamsResults: (peopleAndTeamsResults: PeopleAndTeamsResults) => Action<TState>;
    setPrefetchedRecentSearchResults: (prefetchedRecentSearchResults: SearchLinkProps[]) => Action<TState>;
    setPrefetchedRecentViewsResults: (prefetchedRecentViewsResults: SearchLinkProps[]) => Action<TState>;
    setQuickSearchResults: (quickSearchResults: SearchLinkProps[]) => Action<TState>;
    setRecentSearchResults: (recentSearchResults: SearchLinkProps[]) => Action<TState>;
    setRecentViewsResults: (recentViewsResults: SearchLinkProps[]) => Action<TState>;
    setResultCount: (resultCount: SearchResultCount) => Action<TState>;
    setSelectedInputFilters: (selectedInputFilters: SelectedInputFilters[]) => Action<TState>;
    setShowDropdown: (showDropdown: boolean) => Action<TState>;
}>, void> = createHook(Store, {
	selector: (state) => state.resultCount,
});

export const usePrefetchedRecentViewsResults: HookFunction<SearchLinkProps[] | undefined, BoundActions<TState, {
    setAutoSuggestionResults: (autoSuggestionResults: AutoSuggestionsResults) => Action<TState>;
    setDropdownLoading: (dropdownLoading: boolean) => Action<TState>;
    setPeopleAndTeamsResults: (peopleAndTeamsResults: PeopleAndTeamsResults) => Action<TState>;
    setPrefetchedRecentSearchResults: (prefetchedRecentSearchResults: SearchLinkProps[]) => Action<TState>;
    setPrefetchedRecentViewsResults: (prefetchedRecentViewsResults: SearchLinkProps[]) => Action<TState>;
    setQuickSearchResults: (quickSearchResults: SearchLinkProps[]) => Action<TState>;
    setRecentSearchResults: (recentSearchResults: SearchLinkProps[]) => Action<TState>;
    setRecentViewsResults: (recentViewsResults: SearchLinkProps[]) => Action<TState>;
    setResultCount: (resultCount: SearchResultCount) => Action<TState>;
    setSelectedInputFilters: (selectedInputFilters: SelectedInputFilters[]) => Action<TState>;
    setShowDropdown: (showDropdown: boolean) => Action<TState>;
}>, void> = createHook(Store, {
	selector: (state) => state.prefetchedRecentViewsResults,
});

export const usePrefetchedRecentSearchResults: HookFunction<SearchLinkProps[], BoundActions<TState, {
    setAutoSuggestionResults: (autoSuggestionResults: AutoSuggestionsResults) => Action<TState>;
    setDropdownLoading: (dropdownLoading: boolean) => Action<TState>;
    setPeopleAndTeamsResults: (peopleAndTeamsResults: PeopleAndTeamsResults) => Action<TState>;
    setPrefetchedRecentSearchResults: (prefetchedRecentSearchResults: SearchLinkProps[]) => Action<TState>;
    setPrefetchedRecentViewsResults: (prefetchedRecentViewsResults: SearchLinkProps[]) => Action<TState>;
    setQuickSearchResults: (quickSearchResults: SearchLinkProps[]) => Action<TState>;
    setRecentSearchResults: (recentSearchResults: SearchLinkProps[]) => Action<TState>;
    setRecentViewsResults: (recentViewsResults: SearchLinkProps[]) => Action<TState>;
    setResultCount: (resultCount: SearchResultCount) => Action<TState>;
    setSelectedInputFilters: (selectedInputFilters: SelectedInputFilters[]) => Action<TState>;
    setShowDropdown: (showDropdown: boolean) => Action<TState>;
}>, void> = createHook(Store, {
	selector: (state) => state.prefetchedRecentSearchResults,
});

export const useSelectedInputFilters: HookFunction<SelectedInputFilters[], BoundActions<TState, {
    setAutoSuggestionResults: (autoSuggestionResults: AutoSuggestionsResults) => Action<TState>;
    setDropdownLoading: (dropdownLoading: boolean) => Action<TState>;
    setPeopleAndTeamsResults: (peopleAndTeamsResults: PeopleAndTeamsResults) => Action<TState>;
    setPrefetchedRecentSearchResults: (prefetchedRecentSearchResults: SearchLinkProps[]) => Action<TState>;
    setPrefetchedRecentViewsResults: (prefetchedRecentViewsResults: SearchLinkProps[]) => Action<TState>;
    setQuickSearchResults: (quickSearchResults: SearchLinkProps[]) => Action<TState>;
    setRecentSearchResults: (recentSearchResults: SearchLinkProps[]) => Action<TState>;
    setRecentViewsResults: (recentViewsResults: SearchLinkProps[]) => Action<TState>;
    setResultCount: (resultCount: SearchResultCount) => Action<TState>;
    setSelectedInputFilters: (selectedInputFilters: SelectedInputFilters[]) => Action<TState>;
    setShowDropdown: (showDropdown: boolean) => Action<TState>;
}>, void> = createHook(Store, {
	selector: (state) => state.selectedInputFilters,
});
